import fallbackImage from "@assets/images/fallback-game.svg";
import {useQuery} from "@tanstack/react-query";
import {useMemo} from "react";
import {getTopGamesService} from "@/services/games/gamesList/topGames.service";
import {useGameMetadataStore} from "@/store/useGameMetadataStore";

const TOP_GAMES_LIMIT = 3;
const STALE_TIME = 5 * 60 * 1000;
const GC_TIME = 10 * 60 * 1000;

/**
 * Fetches the top (most-played) games for each provider in parallel.
 * Enriches results with icons/thumbnails from useGameMetadataStore.
 *
 * Backend endpoint: GET /v1/games/top?provider=X&limit=3
 *
 * @param {string[]} providerKeys - Provider keys to fetch top games for
 * @returns {{ topGamesByProvider: Record<string, Array<{id: string, name: string, provider: string, icon: string, thumbnail_url: string}>>, isLoading: boolean }}
 */
export const useProviderTopGames = (providerKeys = []) => {
  const getGameById = useGameMetadataStore(state => state.getGameById);
  const gamesInitialized = useGameMetadataStore(state => state.initialized);

  const stableKey = useMemo(() => [...providerKeys].sort().join(","), [providerKeys]);

  const {data, isPending} = useQuery({
    queryKey: ["providerTopGames", stableKey],
    queryFn: async () => {
      const results = await Promise.all(
        providerKeys.map(async provider => {
          const response = await getTopGamesService({provider, limit: TOP_GAMES_LIMIT});
          return {provider, games: response?.data?.data || []};
        })
      );
      return results;
    },
    enabled: providerKeys.length > 0,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
  });

  const topGamesByProvider = useMemo(() => {
    if (!data || !gamesInitialized) return {};

    const map = {};
    for (const {provider, games} of data) {
      map[provider] = games.map(topGame => {
        const enriched = getGameById(topGame.game_id, topGame.game_provider);
        return {
          id: enriched?.id || topGame.game_id,
          name: enriched?.name || topGame.game_name,
          provider: enriched?.provider || topGame.game_provider,
          icon: enriched?.icon || fallbackImage,
          thumbnail_url: enriched?.thumbnail_url || enriched?.icon || fallbackImage,
        };
      });
    }
    return map;
  }, [data, gamesInitialized, getGameById]);

  return {topGamesByProvider, isLoading: isPending || !gamesInitialized};
};
